import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, ComboBox, TextArea, Theme } from '@carbon/react';
import { Close } from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import { useTheme } from '../../contexts/ThemeContext';
import { SEVERITY_OPTIONS, formatTime } from './workDayUtils';

// Maksimalna dužina beleške koju polje dozvoljava
const MAX_NOTE_LENGTH = 500;

// Koliko rezidenata najviše učitavamo za izbor (opciono tagovanje rezidenta)
const RESIDENTS_PAGE_SIZE = 100;

// Opcija "bez rezidenta" - beleška se ne vezuje ni za jednog rezidenta
const NO_RESIDENT = { id: '', label: 'Bez rezidenta' };

// Normalizuje listu rezidenata iz GET /api/Resident ({ data: [...] } / PagedResponse)
const extractResidents = (body) => {
  const payload = body && body.data !== undefined ? body.data : body;
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : [];
  return list
    .filter((resident) => resident && (resident.id || resident.fullName))
    .map((resident) => ({
      id: resident.id,
      label:
        resident.fullName ||
        `${resident.firstName || ''} ${resident.lastName || ''}`.trim() ||
        'Rezident',
    }));
};

/**
 * ShiftNoteFormDrawer - desni panel sa formom za dodavanje nove beleške smene.
 *
 * Beleška se šalje na POST /api/ShiftNote
 * ({ shiftId, note, severity, residentId }). `severity` je nivo ozbiljnosti
 * (1 = Veoma važno, 2 = Važno, 3 = Normalno), a `residentId` je opcion - kada se
 * ne izabere rezident, prosleđuje se `null` (beleška nije vezana ni za jednog
 * rezidenta). Posle uspešnog čuvanja panel se zatvara i poziva se
 * `onSaved(created)` da se lista beleški ispod trenutne smene odmah osveži.
 * Panel je uvek montiran (radi slide animacije), a `.open` ga uvodi u vidno polje.
 */
const ShiftNoteFormDrawer = ({ shift, onClose, onSaved }) => {
  const { theme } = useTheme();
  const shiftId = shift?.id ?? null;
  const isOpen = shiftId != null;
  const areaId = shift?.areaId ?? null;

  const [noteText, setNoteText] = useState('');
  const [severity, setSeverity] = useState(2);
  const [residentId, setResidentId] = useState('');
  const [residents, setResidents] = useState([]);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Forma se resetuje pri svakom otvaranju/zatvaranju panela (i kad se promeni smena)
  useEffect(() => {
    setNoteText('');
    setSeverity(2);
    setResidentId('');
    setError(null);
    setSaving(false);
  }, [isOpen, shiftId]);

  // Učitaj rezidente (opciono tagovanje) dok je panel otvoren. Ako smena ima
  // oblast, nude se rezidenti te oblasti; u suprotnom svi aktivni rezidenti.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    const fetchResidents = async () => {
      setResidentsLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('pageNumber', '1');
        params.append('pageSize', String(RESIDENTS_PAGE_SIZE));
        if (areaId) params.append('areaId', areaId);
        const response = await axiosInstance.get(`/api/Resident?${params.toString()}`);
        if (!cancelled) setResidents(extractResidents(response.data));
      } catch (err) {
        console.error('Greška pri učitavanju rezidenata:', err);
        if (!cancelled) setResidents([]);
      } finally {
        if (!cancelled) setResidentsLoading(false);
      }
    };

    fetchResidents();
    return () => {
      cancelled = true;
    };
  }, [isOpen, areaId]);

  const residentItems = useMemo(() => [NO_RESIDENT, ...residents], [residents]);
  const selectedResident =
    residentItems.find((item) => item.id === residentId) || NO_RESIDENT;

  // Escape zatvara panel
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const value = noteText.trim();
    if (!value || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await axiosInstance.post('/api/ShiftNote', {
        shiftId,
        note: value,
        severity,
        residentId: residentId || null,
      });
      const body = response.data;
      const created = body && body.data ? body.data : null;
      if (typeof onSaved === 'function') onSaved(created);
      onClose();
    } catch (err) {
      console.error('Greška pri dodavanju beleške smene:', err);
      setError(
        err?.response?.data?.message || 'Belešku nije moguće sačuvati. Pokušajte ponovo.'
      );
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <Theme theme={theme}>
        <div
          className={`wdash-drawer ${isOpen ? 'open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="wdash-shift-note-title"
          aria-hidden={!isOpen}
        >
          <div className="wdash-drawer__head">
            <h3 className="wdash-drawer__title" id="wdash-shift-note-title">
              Nova beleška
            </h3>
            <button
              type="button"
              className="wdash-drawer__icon-btn"
              onClick={onClose}
              aria-label="Zatvori"
            >
              <Close size={18} />
            </button>
          </div>

          <div className="wdash-drawer__body">
            {shift && (
              <p className="wdash-note__shift">
                {shift.area ? `${shift.area} · ` : ''}
                {formatTime(shift.startTime)}–{formatTime(shift.endTime)}
              </p>
            )}

            <form className="wdash-note__form" onSubmit={handleSubmit}>
              <TextArea
                id="wdash-shift-note"
                labelText="Beleška"
                placeholder="Npr. Olivera nije jela dobro."
                value={noteText}
                maxLength={MAX_NOTE_LENGTH}
                rows={5}
                disabled={saving}
                onChange={(event) => setNoteText(event.target.value)}
              />

              {/* Nivo ozbiljnosti beleške (1 = Veoma važno, 2 = Važno, 3 = Normalno) */}
              <div className="wdash-note__severity-field">
                <span className="wdash-note__severity-label">Ozbiljnost</span>
                <div className="wdash-note__severity-tags" role="group" aria-label="Ozbiljnost">
                  {SEVERITY_OPTIONS.map((option) => {
                    const isSelected = severity === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`wdash-note__severity-tag wdash-note__severity-tag--${
                          option.tone
                        } ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => setSeverity(option.value)}
                        disabled={saving}
                        aria-pressed={isSelected}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Opciono tagovanje rezidenta na kog se beleška odnosi */}
              <ComboBox
                id="wdash-shift-note-resident"
                titleText="Rezident (opciono)"
                placeholder="Bez rezidenta"
                items={residentItems}
                itemToString={(item) => (item ? item.label : '')}
                selectedItem={selectedResident}
                onChange={({ selectedItem: item }) =>
                  setResidentId(item && item.id ? item.id : '')
                }
                disabled={saving || residentsLoading}
              />

              {error && <p className="wdash-note__error">{error}</p>}

              <div className="wdash-note__actions">
                <Button kind="primary" type="submit" size="md" disabled={saving || !noteText.trim()}>
                  {saving ? 'Čuvanje...' : 'Dodaj belešku'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Theme>

      {isOpen && <div className="wdash-drawer-overlay" onClick={onClose} />}
    </>,
    document.body
  );
};

export default ShiftNoteFormDrawer;
